// ===================================================================
// 📋 PÁGINA DE ANÁLISE DE RECURSOS - MÓDULO ANALISTA
// ===================================================================

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { styles } from '../../styles/globalStyles';
import { Alert } from '../../components/ui/Alert';
import { Modal } from '../../components/ui/Modal';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { apiClient } from '../../api/apiClient';
import { AppealAnalysisModal } from '../../components/appeals/AppealAnalysisModal';
import { useNotifications } from '../../contexts/NotificationContext';
import { useAdminProcess } from '../../contexts/AdminProcessContext';
import { parseAdminSelectedProcessId } from '../../utils/adminProcessId';
import { getStatusBadgeStyle, getButtonStyle, ACTION_ICONS } from '../../constants/statusTranslations';
import { getAppealTypeLabel } from '../../constants/displayLabels';

export const ResourcesAnalysis = React.memo(({ user }) => {
  const { selectedProcessId, setSelectedProcessId, processes } = useAdminProcess();
  const [resources, setResources] = useState([]);
  const [rejectionReasons, setRejectionReasons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedResource, setSelectedResource] = useState(null);
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCampus, setFilterCampus] = useState('');
  const [filterCourse, setFilterCourse] = useState('');
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [resourceToReturn, setResourceToReturn] = useState(null);
  const [returnReason, setReturnReason] = useState('');

  const { addNotification } = useNotifications();

  // Função removida - validação de período será feita no módulo do candidato

  const loadData = useCallback(async (isBackground = false) => {
    const { id: processId } = parseAdminSelectedProcessId(selectedProcessId);
    if (processId == null) {
      setResources([]);
      if (!isBackground) setLoading(false);
      return;
    }
    try {
      if (!isBackground) setLoading(true);
      const [resourcesData, reasonsData] = await Promise.all([
        apiClient.getAppeals({ processId }),
        apiClient.getRejectionReasons(),
      ]);
      setResources(Array.isArray(resourcesData) ? resourcesData : []);
      setRejectionReasons(Array.isArray(reasonsData) ? reasonsData : []);
    } catch (err) {
      if (!isBackground) setError('Erro ao carregar dados');
    } finally {
      if (!isBackground) setLoading(false);
    }
  }, [user?.id, selectedProcessId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    setFilterCampus('');
    setFilterCourse('');
  }, [selectedProcessId]);

  useEffect(() => {
    if (showAnalysisModal || showReturnModal) return;
    const interval = setInterval(() => loadData(true), 60 * 1000);
    return () => clearInterval(interval);
  }, [loadData, showAnalysisModal, showReturnModal]);

  const getResourceTypeLabel = useCallback((type) => {
    if (!type) return '—';
    return getAppealTypeLabel(type);
  }, []);

  const getResourceTypeStyle = useCallback((type) => {
    const baseStyle = {
      padding: '4px 8px',
      borderRadius: '4px',
      fontSize: '9px',
      fontWeight: '500'
    };
    const t = type ? String(type).toLowerCase() : '';
    switch (t) {
      case 'exemption':
        return { ...baseStyle, backgroundColor: '#DBEAFE', color: '#1E40AF' };
      case 'list':
      case 'enrolled_list':
      case 'registration_list':
        return { ...baseStyle, backgroundColor: '#FEF3C7', color: '#92400E' };
      case 'preliminary_result':
        return { ...baseStyle, backgroundColor: '#E0E7FF', color: '#3730A3' };
      case 'answer_key':
        return { ...baseStyle, backgroundColor: '#FCE7F3', color: '#9D174D' };
      case 'special_attendance':
        return { ...baseStyle, backgroundColor: '#DCFCE7', color: '#166534' };
      case 'analysis':
        return { ...baseStyle, backgroundColor: '#FEF3C7', color: '#92400E' };
      default:
        return { ...baseStyle, backgroundColor: '#F3F4F6', color: '#6B7280' };
    }
  }, []);

  const getStatusLabel = useCallback((status) => {
    switch (status) {
      case 'pending':
        return 'Pendente';
      case 'pending_assignment':
        return 'Pendente de Atribuição';
      case 'assigned':
        return 'Atribuído';
      case 'analyzing':
        return 'Em análise';
      case 'analyzed':
        return 'Analisado';
      case 'approved':
        return 'Aprovado';
      case 'rejected':
        return 'Rejeitado';
      default:
        return status;
    }
  }, []);

  const normalizeResourceTypeForFilter = useCallback((atype) => {
    const t = String(atype || 'exemption').toLowerCase();
    if (t === 'enrolled_list' || t === 'registration_list') return 'list';
    return t;
  }, []);

  const filteredResources = useMemo(() => {
    const procSel = parseAdminSelectedProcessId(selectedProcessId);
    return resources.filter((resource) => {
      if (procSel.id != null && resource.process_id !== procSel.id) return false;
      const resType = normalizeResourceTypeForFilter(resource.resource_type || resource.appeal_type);
      if (filterType && resType !== filterType) return false;
      if (filterStatus && resource.status !== filterStatus) return false;
      if (filterCampus && String(resource.course_campus || '') !== String(filterCampus)) return false;
      if (filterCourse && String(resource.course_name || '') !== String(filterCourse)) return false;
      return true;
    });
  }, [resources, selectedProcessId, filterType, filterStatus, filterCampus, filterCourse, normalizeResourceTypeForFilter]);

  const campusFilterOptions = useMemo(() => {
    const procSel = parseAdminSelectedProcessId(selectedProcessId);
    const campuses = new Set();
    resources.forEach((resource) => {
      if (procSel.id != null && resource.process_id !== procSel.id) return;
      if (resource.course_campus) campuses.add(String(resource.course_campus));
    });
    return Array.from(campuses).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [resources, selectedProcessId]);

  const courseFilterOptions = useMemo(() => {
    const procSel = parseAdminSelectedProcessId(selectedProcessId);
    const courses = new Set();
    resources.forEach((resource) => {
      if (procSel.id != null && resource.process_id !== procSel.id) return;
      if (resource.course_name) courses.add(String(resource.course_name));
    });
    return Array.from(courses).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [resources, selectedProcessId]);

  const pendingResourcesCount = useMemo(() => {
    return filteredResources.filter((r) => {
      const s = r.status;
      return s === 'pending' || s === 'assigned' || s === 'analyzing' || s === 'pending_assignment';
    }).length;
  }, [filteredResources]);

  const appealStatusBadgeKey = useCallback((status) => {
    const s = (status || '').toLowerCase();
    if (s === 'assigned') return 'assigned';
    if (s === 'analyzing') return 'analyzing';
    if (s === 'approved') return 'approved';
    if (s === 'rejected') return 'rejected';
    if (s === 'pending_assignment' || s === 'pending') return 'pending';
    return s || 'pending';
  }, []);

  const handleAcceptAppealTask = useCallback(async (resource) => {
    try {
      const { error } = await apiClient.acceptAppealAssignment(resource.id);
      if (error) throw new Error(error);
      addNotification('Sucesso', 'Tarefa aceita. Situação: Em análise', 'success');
      await loadData(true);
    } catch (e) {
      addNotification('Erro', e.message || 'Erro ao aceitar tarefa', 'error');
    }
  }, [addNotification, loadData]);

  const openReturnAppealModal = useCallback((resource) => {
    setResourceToReturn(resource);
    setReturnReason('');
    setShowReturnModal(true);
  }, []);

  const handleReturnAppealTask = useCallback(async () => {
    if (!resourceToReturn) return;
    const r = returnReason.trim();
    if (!r) {
      addNotification('Atenção', 'Informe o motivo da devolução.', 'error');
      return;
    }
    try {
      const { error } = await apiClient.returnAppealAssignment(resourceToReturn.id, r);
      if (error) throw new Error(error);
      addNotification('Sucesso', 'Tarefa devolvida ao administrador', 'success');
      setShowReturnModal(false);
      setResourceToReturn(null);
      setReturnReason('');
      await loadData();
    } catch (e) {
      addNotification('Erro', e.message || 'Erro ao devolver tarefa', 'error');
    }
  }, [resourceToReturn, returnReason, addNotification, loadData]);

  const handleAnalyzeResource = useCallback((resource) => {
    if (resource.status === 'assigned') {
      addNotification('Atenção', 'Aceite a tarefa (botão Aceitar) antes de abrir a análise.', 'warning');
      return;
    }
    setSelectedResource(resource);
    setShowAnalysisModal(true);
  }, [addNotification]);

  const handleCloseAppealAnalysis = useCallback(() => {
    setShowAnalysisModal(false);
    setSelectedResource(null);
  }, []);

  if (loading && !showAnalysisModal) {
    return <LoadingSpinner />;
  }

  return (
    <div style={styles.contentContainer}>
      <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: '#111827', marginBottom: '24px' }}>
        📋 Análise de Recursos
      </h2>

      {error && <Alert type="error" message={error} onClose={() => setError('')} />}

      {/* Filtros */}
      <div style={{ ...styles.card, ...styles.mb6 }}>
        <h3 style={styles.cardTitle}>🔍 Filtros</h3>
        <div style={{ ...styles.flex, ...styles.gap4, ...styles.flexWrap }}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Processo Seletivo</label>
            <select
              value={selectedProcessId}
              onChange={setSelectedProcessId}
              style={styles.select}
            >
              <option value="">Selecione um processo</option>
              {processes.map(process => (
                <option key={process.id} value={process.id}>
                  {process.name}
                </option>
              ))}
            </select>
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Tipo de Recurso</label>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              style={styles.select}
            >
              <option value="">Todos os tipos</option>
              <option value="exemption">Recurso de isenção</option>
              <option value="list">Recurso contra a Lista de inscritos</option>
              <option value="preliminary_result">Recurso de Resultado Preliminar</option>
              <option value="answer_key">Recurso contra o Gabarito</option>
              <option value="special_attendance">Atendimento especial</option>
            </select>
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Situação</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              style={styles.select}
            >
              <option value="">Todas as situações</option>
              <option value="assigned">Atribuído (aguardando aceite)</option>
              <option value="analyzing">Em análise</option>
              <option value="approved">Aprovado</option>
              <option value="rejected">Rejeitado</option>
            </select>
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Unidade</label>
            <select value={filterCampus} onChange={(e) => setFilterCampus(e.target.value)} style={styles.select}>
              <option value="">Todas as unidades</option>
              {campusFilterOptions.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Curso</label>
            <select value={filterCourse} onChange={(e) => setFilterCourse(e.target.value)} style={styles.select}>
              <option value="">Todos os cursos</option>
              {courseFilterOptions.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Lista de Recursos */}
      <div style={styles.card}>
        <div style={{ ...styles.flexBetween, ...styles.mb4 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
            <h3 style={styles.cardTitle}>Recursos Atribuídos ({filteredResources.length})</h3>
            <span style={{ fontSize: '14px', color: '#374151' }}>
              <strong>Legenda:</strong> Aceitar · Devolver · {ACTION_ICONS.analyze} Analisar · {ACTION_ICONS.edit} Editar
            </span>
          </div>
          <span style={{ fontSize: '14px', color: '#6B7280' }}>
            Total: {filteredResources.length} | Pendentes: {pendingResourcesCount}
          </span>
        </div>

        {filteredResources.length > 0 ? (
          <div style={{ ...styles.tableContainer, ...styles.tableScrollContainer }}>
            <table style={{ ...styles.table, width: '100%', fontSize: '10px', hyphens: 'none' }}>
              <thead>
                <tr>
                  <th style={{ ...styles.tableHeaderCell, fontSize: '9px', padding: '8px 6px', width: '45px' }}>Nº</th>
                  <th style={{ ...styles.tableHeaderCell, fontSize: '10px', padding: '8px 10px' }}>Candidato</th>
                  <th style={{ ...styles.tableHeaderCell, fontSize: '10px', padding: '8px 10px' }}>Curso</th>
                  <th style={{ ...styles.tableHeaderCell, fontSize: '10px', padding: '8px 10px' }}>Unidade</th>
                  <th style={{ ...styles.tableHeaderCell, fontSize: '10px', padding: '8px 10px' }}>Tipo de Recurso</th>
                  <th style={{ ...styles.tableHeaderCell, fontSize: '10px', padding: '8px 10px' }}>Situação atual</th>
                  <th style={{ ...styles.tableHeaderCell, fontSize: '10px', padding: '8px 10px' }}>Situação</th>
                  <th style={{ ...styles.tableHeaderCell, fontSize: '10px', padding: '8px 10px' }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {filteredResources.map((resource) => {
                  const process = processes.find(p => p.id === resource.process_id);
                  
                  return (
                    <tr key={resource.id} style={styles.tableRow}>
                      <td style={{ ...styles.tableCell, fontSize: '10px', padding: '8px 6px', fontWeight: '600', color: '#374151' }}>
                        #{resource.application_id}
                      </td>
                      <td style={{ ...styles.tableCell, fontSize: '10px', padding: '8px 10px' }}>
                        <div style={{ fontWeight: '600', color: '#1F2937', fontSize: '10px' }}>
                          {resource.candidate_name}
                        </div>
                        <div style={{ fontSize: '9px', color: '#6B7280' }}>
                          CPF: {resource.candidate_cpf}
                        </div>
                      </td>
                      <td style={{ ...styles.tableCell, fontSize: '10px', padding: '8px 10px' }}>
                        <div>
                          <div style={{ fontWeight: '600', color: '#111827', fontSize: '10px' }}>
                            {resource.course_name || 'N/A'}
                          </div>
                        </div>
                      </td>
                      <td style={{ ...styles.tableCell, fontSize: '10px', padding: '8px 10px' }}>{resource.course_campus || '—'}</td>
                      <td style={{ ...styles.tableCell, fontSize: '10px', padding: '8px 10px' }}>
                        <span style={getResourceTypeStyle(resource.resource_type || resource.appeal_type)}>
                          {getResourceTypeLabel(resource.resource_type || resource.appeal_type)}
                        </span>
                      </td>
                      <td style={{ ...styles.tableCell, fontSize: '10px', padding: '8px 10px' }}>
                        <div style={{ 
                          fontSize: '10px', 
                          color: (resource.current_situation_label || '').toLowerCase().includes('indeferido') ? '#EF4444' : (resource.current_situation_label || '').toLowerCase().includes('deferido') ? '#059669' : '#6B7280'
                        }}>
                          {resource.current_situation_label || '—'}
                        </div>
                        {resource.current_reason_title && (
                          <div style={{ fontSize: '9px', color: '#6B7280' }}>
                            Motivo: {resource.current_reason_title}
                          </div>
                        )}
                      </td>
                      <td style={{ ...styles.tableCell, fontSize: '10px', padding: '8px 10px' }}>
                        <span style={{ ...getStatusBadgeStyle(appealStatusBadgeKey(resource.status)), ...styles.tableCellBadge, fontSize: '9px', padding: '4px 8px' }} title={getStatusLabel(resource.status)}>
                          {getStatusLabel(resource.status)}
                        </span>
                      </td>
                      <td style={{ ...styles.tableCell, fontSize: '10px', padding: '8px 10px', textAlign: 'center' }}>
                        <div style={{ ...styles.flex, ...styles.gap2, justifyContent: 'center', flexWrap: 'wrap' }}>
                          {resource.status === 'assigned' && (
                            <>
                              <button
                                type="button"
                                onClick={() => handleAcceptAppealTask(resource)}
                                style={getButtonStyle('primary', 'small')}
                                title="Aceitar tarefa (passa para Em análise)"
                              >
                                ✅ Aceitar
                              </button>
                              <button
                                type="button"
                                onClick={() => openReturnAppealModal(resource)}
                                style={getButtonStyle('secondary', 'small')}
                                title="Devolver tarefa para o admin"
                              >
                                ↩️ Devolver
                              </button>
                              <button
                                type="button"
                                disabled
                                style={{ ...getButtonStyle('secondary', 'small'), opacity: 0.5, cursor: 'not-allowed' }}
                                title="Aceite a tarefa para analisar"
                              >
                                {ACTION_ICONS.analyze}
                              </button>
                            </>
                          )}
                          {resource.status === 'analyzing' && (
                            <>
                              <button
                                type="button"
                                onClick={() => openReturnAppealModal(resource)}
                                style={getButtonStyle('secondary', 'small')}
                                title="Devolver tarefa para o admin"
                              >
                                ↩️ Devolver
                              </button>
                              <button
                                type="button"
                                onClick={() => handleAnalyzeResource(resource)}
                                style={getButtonStyle('primary', 'small')}
                                title="Abrir formulário de análise"
                              >
                                {ACTION_ICONS.analyze}
                              </button>
                            </>
                          )}
                          {(resource.status === 'approved' || resource.status === 'rejected') && (
                            <>
                              <button
                                type="button"
                                onClick={() => handleAnalyzeResource(resource)}
                                style={getButtonStyle('secondary', 'small')}
                                title="Editar análise"
                              >
                                {ACTION_ICONS.edit} Editar
                              </button>
                              <button
                                type="button"
                                onClick={() => openReturnAppealModal(resource)}
                                style={getButtonStyle('secondary', 'small')}
                                title="Devolver ao administrador para reanálise (ex.: decisão será revista pelo admin)"
                              >
                                ↩️ Devolver p/ admin
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={styles.emptyState}>
            <p>Nenhum recurso encontrado</p>
          </div>
        )}
      </div>

      <AppealAnalysisModal
        isOpen={showAnalysisModal}
        onClose={handleCloseAppealAnalysis}
        resource={selectedResource}
        user={user}
        rejectionReasons={rejectionReasons}
        addNotification={addNotification}
        onSaved={loadData}
      />

      <Modal
        isOpen={showReturnModal}
        onClose={() => { setShowReturnModal(false); setResourceToReturn(null); setReturnReason(''); }}
        title="↩️ Devolver tarefa"
      >
        {resourceToReturn && (
          <div>
            <p style={{ marginBottom: '16px', fontSize: '14px', color: '#6B7280' }}>
              {(resourceToReturn.status === 'approved' || resourceToReturn.status === 'rejected')
                ? 'Este recurso já tem decisão. Use quando o administrador solicitar revisão: ele voltará à fila para nova análise (por exemplo pelo próprio admin). A publicação da resposta ao candidato, se existia, será cancelada até nova decisão e nova publicação. Informe o motivo (obrigatório).'
                : 'O recurso voltará para a fila do administrador para nova atribuição. Informe o motivo (obrigatório).'}
            </p>
            <div style={{ marginBottom: '16px' }}>
              <label style={styles.label}>Motivo da devolução</label>
              <textarea
                value={returnReason}
                onChange={(e) => setReturnReason(e.target.value)}
                style={styles.textarea}
                placeholder="Ex.: Atribuição incorreta, sobrecarga, documentação incompleta..."
                rows={4}
              />
            </div>
            <div style={{ ...styles.flex, ...styles.gap4, justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => { setShowReturnModal(false); setResourceToReturn(null); setReturnReason(''); }}
                style={{ ...styles.button, ...styles.buttonSecondary }}
              >
                Cancelar
              </button>
              <button type="button" onClick={handleReturnAppealTask} style={{ ...styles.button, ...styles.buttonPrimary }}>
                Devolver
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
});
